import { format, formatDistanceToNowStrict, parseISO, type Locale } from 'date-fns';
import { de, enGB, tr } from 'date-fns/locale';

const DATE_LOCALES: Record<string, Locale> = {
  tr,
  en: enGB,
  de,
};

const DATE_TIME_PATTERNS: Record<string, string> = {
  tr: 'dd.MM.yyyy HH:mm',
  en: 'dd/MM/yyyy HH:mm',
  de: 'dd.MM.yyyy HH:mm',
};

function resolveLocale(language?: string): Locale {
  if (!language) return tr;
  const base = language.split('-')[0]?.toLowerCase() ?? 'tr';
  return DATE_LOCALES[base] ?? tr;
}

/** Format an ISO date string with a default pattern. Returns "—" on invalid input. */
export function formatDate(
  value: string | Date | null | undefined,
  pattern = 'yyyy-MM-dd HH:mm',
  language?: string,
): string {
  if (!value) return '—';
  try {
    const d = typeof value === 'string' ? parseISO(value) : value;
    return format(d, pattern, { locale: resolveLocale(language) });
  } catch {
    return '—';
  }
}

/** Locale-aware date+time for UI (TR: 17.05.2026 11:55). */
export function formatDateTime(
  value: string | Date | null | undefined,
  language = 'tr',
): string {
  if (!value) return '—';
  try {
    const d = typeof value === 'string' ? parseISO(value) : value;
    const base = language.split('-')[0]?.toLowerCase() ?? 'tr';
    const pattern = DATE_TIME_PATTERNS[base] ?? 'dd.MM.yyyy HH:mm';
    return format(d, pattern, { locale: resolveLocale(language) });
  } catch {
    return '—';
  }
}

export function formatRelative(value: string | Date | null | undefined): string {
  if (!value) return '—';
  try {
    const d = typeof value === 'string' ? parseISO(value) : value;
    return `${formatDistanceToNowStrict(d, { addSuffix: true })}`;
  } catch {
    return '—';
  }
}

const ENERGY_FMT = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
const POWER_FMT = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 });

export function formatKWh(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${ENERGY_FMT.format(value)} kWh`;
}

export function formatKW(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${POWER_FMT.format(value)} kW`;
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || Number.isNaN(seconds) || seconds <= 0) return '0m';
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

export function formatCurrency(value: number | null | undefined, currency = 'TRY'): string {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
}
